import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { auth } from './firebaseConfig';
import apiService from '../services/ApiService';

/**
 * Stato di autenticazione admin.
 *
 * IMPORTANTE (sicurezza):
 * - L'utente loggato Firebase NON e' automaticamente admin. Il check
 *   role="Admin" arriva dal backend tramite /api/admin/me (chiamato
 *   dopo ogni cambio auth).
 * - Se il backend risponde 403 → utente loggato Firebase ma non admin
 *   nel DB → mostriamo AccessDeniedPage; non lasciamo proseguire.
 * - Non ci fidiamo MAI di un claim "role" arrivato dal token Firebase
 *   in chiaro: il claim e' un suggerimento; il source-of-truth e' il DB,
 *   verificato server-side ad ogni request grazie alla policy "AdminOnly".
 */
export type AdminStatus = 'loading' | 'anonymous' | 'admin' | 'denied' | 'error';

interface AuthCtx {
    user: User | null;
    status: AdminStatus;
    error: string | null;
    signOutAdmin: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
    user: null, status: 'loading', error: null, signOutAdmin: async () => {},
});

export const useAuth = () => useContext(Ctx);

interface MeResponse {
    userId: number;
    email: string;
    role: string;
    displayName?: string;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [status, setStatus] = useState<AdminStatus>('loading');
    const [error, setError] = useState<string | null>(null);

    const verifyAdmin = useCallback(async (fbUser: User) => {
        try {
            const me = await apiService.get<MeResponse>('/api/admin/me');
            if (me.role === 'Admin') setStatus('admin');
            else setStatus('denied');
        } catch (e: any) {
            // 403 dal BE = utente Firebase autenticato ma NON admin nel DB.
            if (e?.response?.status === 403 || e?.response?.status === 401) {
                setStatus('denied');
            } else {
                setStatus('error');
                setError(e?.message ?? 'Errore verifica ruolo admin');
            }
        } finally {
            // Mai loggare l'oggetto user completo: contiene token.
            // eslint-disable-next-line no-console
            console.log('[admin auth] verify done for', fbUser.email);
        }
    }, []);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async fbUser => {
            setUser(fbUser);
            if (!fbUser) { setStatus('anonymous'); return; }
            setStatus('loading');
            await verifyAdmin(fbUser);
        });
        return () => unsub();
    }, [verifyAdmin]);

    const signOutAdmin = useCallback(async () => {
        await signOut(auth);
        // Cleanup difensivo: cancella sessionStorage/localStorage
        // dell'app admin per evitare residui (token cache, ecc).
        try {
            sessionStorage.clear();
            // Non tocchiamo localStorage perche' Firebase ci mette dentro
            // cose tecniche; setPersistence(SESSION) garantisce che il
            // token sparisca quando si chiude il browser.
        } catch { /* ignore */ }
    }, []);

    return (
        <Ctx.Provider value={{ user, status, error, signOutAdmin }}>
            {children}
        </Ctx.Provider>
    );
};
